import { useState } from 'react'
import React from 'react'

import { F0Dialog, StandardLayout } from '@factorialco/f0-react'
import { CheckDouble, MoneyBag } from '@factorialco/f0-react/icons/app'
import { AttendanceSidepanelCompensationBlockFragment } from 'generated/resources/graphql'
import { Moment } from 'moment'

import i18n from 'modules/core/lib/i18n'

import CompensationModalWrapper from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationModalWrapper'
import { useEmployeeSidepanelNavigation } from 'modules/attendance/hooks/useEmployeeSidepanelNavigation'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { SidePanelTab } from 'modules/attendance/lib/sidepanelTabTypes'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { buildAllCompensationBlocksPayload, PeriodState } from 'modules/attendance/lib/types'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

import Overview from './Overview'
import TimeCategoriesBreakdown from './TimeCategoriesBreakdown'
import { generateDailyTimeCategories } from './TimeCategoriesBreakdown/utils'

type Props = {
  tabFromNavigation: SidePanelTab
  timeSheetData: ReturnType<typeof useTimesheetData>
  timeSheetDate: Moment
  employeeId: number
}

const CompensationPannel = ({
  tabFromNavigation,
  timeSheetData,
  timeSheetDate,
  employeeId,
}: Props) => {
  const { employeesSettings, balances, reviewsStats } = timeSheetData

  const dailyTimeCategories = generateDailyTimeCategories(balances)
  const isPeriodApproved = reviewsStats?.state === PeriodState.approved

  // TODO: Remove this logic once the feature flag dev_compensations is removed
  const payrollCompensationEnabled =
    EmployeesSettingsUtils.hasPayrollCompensationEnabled(employeesSettings)

  const timeoffCompensationEnabled =
    EmployeesSettingsUtils.hasTimeoffCompensationEnabled(employeesSettings)

  const compensationEnabled = payrollCompensationEnabled || timeoffCompensationEnabled

  const allowManualAllocationEnabled =
    EmployeesSettingsUtils.hasAllowManualAllocationEnabled(employeesSettings)

  const hasComplementaryHoursFeature =
    TimesheetFeatures.COMPLEMENTARY_HOURS in (timeSheetData.featureByName ?? {})
  const useComplementaryHoursLabels =
    hasComplementaryHoursFeature &&
    timeSheetData.isPartTime &&
    !!timeSheetData.currentEmployeesSetting?.enableComplementaryHours

  const [selectedCompensationBlocks, setSelectedCompensationBlocks] = useState<
    AttendanceSidepanelCompensationBlockFragment[]
  >([])
  const [hasCompensationErrors, setHasCompensationErrors] = useState(false)
  const [bohAllocation, setBOHAllocation] = useState<{
    timeOffMinutes: number
    payrollMinutes: number
  } | null>(null)

  const [openCompensationModal, setOpenCompensationModal] = React.useState(false)

  const { featureAvailability: hasManualAllocation } = useAttendanceFeatures(
    'dev_time_tracking_manual_allocation'
  )

  const { closePanel } = useEmployeeSidepanelNavigation({
    tabFromNavigation,
    compensationEnabled,
    timeSheetDate,
    employeeId,
  })

  const handleCompensate = () => {
    setOpenCompensationModal(true)
  }

  const defaultTabId = 'compensation-overview'

  const [activeTabId, setActiveTabId] = useState(defaultTabId)

  return (
    <>
      <F0Dialog
        isOpen
        onClose={openCompensationModal ? () => {} : closePanel}
        position="right"
        title={i18n.t('clock_in.sidepanel.compensation.compensation_details')}
        primaryAction={
          activeTabId === 'compensation-overview' && selectedCompensationBlocks.length > 0
            ? {
                label: isPeriodApproved
                  ? i18n.t('time_tracking.table_collection.employee_summary.actions.compensate')
                  : i18n.t('clock_in.sidepanel.compensation_overview.approve_and_compensate'),
                icon: isPeriodApproved ? MoneyBag : CheckDouble,
                onClick: handleCompensate,
                disabled: hasCompensationErrors,
              }
            : undefined
        }
        tabs={[
          {
            label: i18n.t('clock_in.sidepanel.compensation_overview.label'),
            id: 'compensation-overview',
          },
          {
            label: i18n.t('clock_in.sidepanel.compensation_time_categories_breakdown.label'),
            id: 'compensation-time-categories',
          },
        ]}
        activeTabId={activeTabId}
        setActiveTabId={setActiveTabId}
      >
        <StandardLayout>
          <div data-testid="timesheet-compensation-dialog">
            {activeTabId === 'compensation-overview' && (
              <Overview
                setSelectedCompensationBlocks={setSelectedCompensationBlocks}
                onHasErrorsChange={setHasCompensationErrors}
                employeeId={employeeId}
                payrollCompensationEnabled={payrollCompensationEnabled}
                timeoffCompensationEnabled={timeoffCompensationEnabled}
                useComplementaryHoursLabels={useComplementaryHoursLabels}
                allowManualAllocationEnabled={allowManualAllocationEnabled}
                bohAllocation={bohAllocation}
                onBOHAllocationChange={setBOHAllocation}
              />
            )}
            {activeTabId === 'compensation-time-categories' && (
              <TimeCategoriesBreakdown dailyTimeCategories={dailyTimeCategories} />
            )}
          </div>
        </StandardLayout>
      </F0Dialog>

      <CompensationModalWrapper
        employeeId={employeeId}
        selectedCompensationBlocks={selectedCompensationBlocks}
        timeSheetData={timeSheetData}
        timeSheetDate={timeSheetDate}
        onClose={closePanel}
        openModal={openCompensationModal}
        setOpenModal={setOpenCompensationModal}
        compensationBlockIds={
          selectedCompensationBlocks.length > 0
            ? selectedCompensationBlocks.map((cb) => cb.id)
            : undefined
        }
        compensationBlocks={
          hasManualAllocation && selectedCompensationBlocks.length > 0
            ? buildAllCompensationBlocksPayload(selectedCompensationBlocks, bohAllocation)
            : undefined
        }
      />
    </>
  )
}

export default CompensationPannel
